import { clsx } from '@alexis/helpers/clsx';
import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';

import CircleTickIcon from '../icons/circle_tick.svg';
import WarningIcon from '../icons/warning.svg';
import styles from '../styles/components/DateInput.module.scss';

interface DateInputProps {
  /**
   * @variation bday
   * @variation cc-exp
   */
  autoComplete?: string;
  className?: string;
  dateFormat: 'DD/MM/YYYY' | 'MM/YY';
  hasError?: boolean;
  id?: string;
  isRtl: boolean;
  isValidated: boolean;
  message?: string;
  name?: string;
  onBlur?(): void;
  onChange(value: string): void;
  placeholder: string;
  style?: React.CSSProperties;
  value?: string;
}

const DateInput = forwardRef<HTMLDivElement, DateInputProps>(
  (
    {
      autoComplete,
      className,
      dateFormat,
      hasError,
      id,
      isRtl,
      isValidated,
      message,
      name,
      onBlur,
      onChange,
      placeholder,
      style,
      value,
    },
    ref,
  ): JSX.Element => {
    const inputPlaceholderRef = useRef<HTMLDivElement>(null);

    const [isFocus, setIsFocus] = useState<boolean>(false);

    const [inputPlaceholder, setInputPlaceholder] = useState<string>('');

    const [inputPaddingRight, setInputPaddingRight] = useState<number>(0);

    useEffect(() => {
      if (isRtl && !!inputPlaceholderRef.current) {
        const inputPlaceholderWidth = inputPlaceholderRef.current.getBoundingClientRect().width;

        setInputPaddingRight(inputPlaceholderWidth);
      }
    }, [inputPlaceholder, isRtl, inputPlaceholderRef]);

    useEffect(() => {
      let dateFormatPlaceholder = dateFormat.slice();

      if (!!value) {
        dateFormatPlaceholder = dateFormatPlaceholder.replace(
          dateFormatPlaceholder.substring(0, value.length),
          '',
        );
      }
      setInputPlaceholder(dateFormatPlaceholder);
    }, [value]);

    const handleBlur = useCallback<() => void>(() => {
      setIsFocus(false);

      if (!!onBlur) {
        onBlur();
      }
    }, [onBlur]);

    const handleChange = useCallback<(event: React.ChangeEvent<HTMLInputElement>) => void>(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        if (value.length > dateFormat.length) {
          return;
        }

        const valueWithoutSlash = value.replace(/[\/]/g, '');
        const splitedNumbers = valueWithoutSlash.match(/[\d]{1,4}/g);

        if (!!splitedNumbers) {
          const firstSplitedNumber = splitedNumbers[0];

          let formattedValue = firstSplitedNumber.match(/[\d]{1,2}/g)!.join('/');

          if (splitedNumbers.length === 2) {
            formattedValue = [formattedValue, splitedNumbers[1]].join('/');
          }

          onChange(formattedValue);
        } else {
          onChange(event.target.value);
        }
      },
      [onChange],
    );

    const defaultInputId = 'inputId';

    return (
      <div
        ref={ref}
        className={clsx(
          styles.container,
          className,
          isRtl && styles.rtl,
          isFocus && styles.focus,
          isValidated && styles.validated,
          !!hasError && styles.error,
        )}
        style={style}
      >
        <div className={styles.inputContainer}>
          <div
            ref={inputPlaceholderRef}
            className={clsx(styles.inputPlaceholder, (!!value || isFocus) && styles.show)}
          >
            {!isRtl ? <span>{value}</span> : null}
            {inputPlaceholder}
          </div>

          <input
            autoComplete={autoComplete}
            id={id ?? defaultInputId}
            inputMode='numeric'
            name={name}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={() => setIsFocus(true)}
            spellCheck={false}
            style={isRtl ? { paddingRight: `${inputPaddingRight}px` } : {}}
            value={value}
          />

          <label htmlFor={id ?? defaultInputId}>{placeholder}</label>

          {isValidated && !hasError ? <CircleTickIcon /> : null}

          {isValidated && !!hasError ? <WarningIcon /> : null}
        </div>

        <div className={styles.message}>{message}</div>
      </div>
    );
  },
);

export default DateInput;
